import type { SeriesName, SeriesPoint } from "@/api/recordsSeries";
import { PIPELINE_COLORS, SERIES_COLORS } from "@/components/seriesPalette";
import { momentLabel } from "@/utils/moments";

// The lines a records chart draws (TTTL-304), assembled from the fence and
// the series each pipeline's fetch returned. One pipeline draws its lines
// as the first charts did: the Figma hues, watched under a wash. A
// comparison draws one line per pipeline — the fence's one series — in
// the overlay palette, by the pipeline's place in the fence, so a
// pipeline whose fetch failed leaves a gap in the palette rather than
// repainting the rest.

/** One line on the plot: its legend key, its colour, the wash under it,
 *  and a value per moment, null where nobody was watching. */
export interface ChartLine {
  label: string;
  color: string;
  wash?: string;
  values: Array<number | null>;
}

/** What to draw, as the fence names it: the pipelines in order, and the
 *  lines of each — one line when there are several pipelines. */
export interface ChartSpec {
  pipelines: string[];
  series: SeriesName[];
}

export interface ChartData {
  /** The moments along the axis: the first drawn pipeline's. Every
   *  pipeline was asked for the same window and count, so the others'
   *  moments line up by index. */
  moments: string[];
  lines: ChartLine[];
}

const SERIES_LABELS: Record<SeriesName, string> = {
  watched: "records watched",
  dropped: "records dropped",
  incomplete: "records incomplete",
};

export function chartLines(
  chart: ChartSpec,
  fetched: ReadonlyMap<string, SeriesPoint[]>,
): ChartData {
  const drawn = chart.pipelines.filter((p) => fetched.has(p));
  const first = drawn[0];
  if (first === undefined) return { moments: [], lines: [] };
  const moments = (fetched.get(first) ?? []).map((p) => p.at);
  const valuesOf = (pipeline: string, name: SeriesName) => {
    const points = fetched.get(pipeline) ?? [];
    return moments.map((_, i) => points[i]?.[name] ?? null);
  };
  if (chart.pipelines.length === 1) {
    return {
      moments,
      lines: chart.series.map((name) => ({
        label: SERIES_LABELS[name],
        color: SERIES_COLORS[name],
        ...(name === "watched" ? { wash: SERIES_COLORS.watchedFill } : {}),
        values: valuesOf(first, name),
      })),
    };
  }
  const name = chart.series[0] ?? "watched";
  const lines = chart.pipelines.flatMap((pipeline, slot) => {
    if (!fetched.has(pipeline)) return [];
    return [
      {
        label: pipeline,
        color: PIPELINE_COLORS[slot] ?? PIPELINE_COLORS[0],
        values: valuesOf(pipeline, name),
      },
    ];
  });
  return { moments, lines };
}

/** Whether there is anything to draw: false when every line is unknown at
 *  every moment. */
export function hasCounts(lines: ChartLine[]): boolean {
  return lines.some((line) => line.values.some((v) => v !== null));
}

/** The chart in words, for a reader who cannot see the canvas: the window
 *  and each line's range, "records watched 40 to 320", or "unknown" for a
 *  line the engine gave nothing for. */
export function describeLines({ moments, lines }: ChartData): string {
  const first = moments[0];
  const last = moments[moments.length - 1];
  if (first === undefined || last === undefined) {
    return "Records over time: no moments.";
  }
  const window = `${moments.length} moments from ${momentLabel(first)} to ${momentLabel(last)}`;
  const ranges = lines.map((line) => `${line.label} ${rangeOf(line.values)}`);
  return `Records over time, ${window}: ${ranges.join(", ")}.`;
}

function rangeOf(values: Array<number | null>): string {
  const known = values.filter((v): v is number => v !== null);
  if (known.length === 0) return "unknown";
  return `${Math.min(...known)} to ${Math.max(...known)}`;
}
